import React, { ChangeEvent, FC, Fragment, useRef, useState } from 'react';
import { Chip, IconButton, Menu, MenuItem, Typography } from '@material-ui/core';
import moment from 'moment';
import { MdEdit as EditIcon } from '@react-icons/all-files/md/MdEdit';
import { MdAdd as AddIcon } from '@react-icons/all-files/md/MdAdd';
import { MdMoreVert as MenuIcon } from '@react-icons/all-files/md/MdMoreVert';
import Attachment from './components/Attachment';
import { VerticalTimelineElement } from 'react-vertical-timeline-component';
import WallEvents from '../../../../../../../../../../interfaces/WallEvents';
import { Item } from '../../../../../../../../../../interfaces/api/ActivityWall';
import Document from '../../../../../../../../../../interfaces/api/Document';
import { createUseStyles } from '../../../../../../../../../../theming';
import { Skeleton } from '@material-ui/lab';
import ConfirmationDialog from '../../../../../../../../../../components/ConfirmationDialog';
import Employee from '../../../../../../../../../../interfaces/api/Employee';
import PostEditionDialog from './components/PostEditionDialog';
import { useRecoilValue } from 'recoil';
import { employeeState } from '../../../../../../../../../../recoil/selectors/employees';

interface Props {
  item: Item;
  attachments: Document[];
  employeeId?: Employee['id'];
  error?: string;
  onDeleteItem: () => void;
  onEditItem: (title: string, comment: string) => void;
  onAddAttachment: (attachment: File) => void;
  onRemoveAttachment: (id: string) => void;
  attachmentsLoading?: boolean;
}

const useStyles = createUseStyles({
  message: {
    background: '#e8e8e8',
    border: '1px solid #dbdbdb',
    borderRadius: 3,
    padding: 15,
    fontWeight: 'initial !important',
  },
  attachments: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: 10,
    '& > *': {
      marginBottom: '8px !important',
      marginRight: '8px !important',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: -7,
  },
  actions: {
    '& > button': {
      padding: '5px !important',
    },
  },
});

const TimelineElement: FC<Props> = ({
  item,
  employeeId,
  onAddAttachment,
  onRemoveAttachment,
  onDeleteItem,
  onEditItem,
  attachments,
  attachmentsLoading,
  error,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpened = Boolean(menuAnchorEl);

  const [isEditionDialogOpen, setIsEditionDialogOpen] = useState(false);

  const [fileIdToDelete, setFileIdToDelete] = useState<string>();
  const inputFileRef = useRef<HTMLInputElement>(null);
  const classes = useStyles();

  const employee = useRecoilValue(employeeState(employeeId));
  const event = WallEvents[item.type];
  const Icon = event.icon;

  const handleClickMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleDeleteItem = () => {
    handleCloseMenu();
    onDeleteItem();
  };

  const getAttachment = (id: string) => attachments.find((attachment) => attachment.id === id);

  const handleFileDelete = async () => {
    await onRemoveAttachment(fileIdToDelete!);
    setFileIdToDelete(undefined);
  };

  const onPickImage = async (e: ChangeEvent<HTMLInputElement>) => {
    const targetFiles = e.target?.files || [];

    if (targetFiles.length > 0) {
      onAddAttachment(targetFiles[0]);
    }
  };

  return (
    <VerticalTimelineElement
      className='vertical-timeline-element--work'
      contentStyle={{ boxShadow: '0px 0px 7px 2px rgba(0,0,0,0.2)' }}
      iconStyle={{ background: event.color, color: '#fff' }}
      icon={<Icon />}
    >
      <div className={classes.header}>
        <Typography variant='h6'>{item.payload?.title || ''}</Typography>
        <div className={classes.actions}>
          {item.type === 'POST' && (
            <>
              <IconButton size='medium' onClick={() => setIsEditionDialogOpen(true)}>
                <EditIcon size={19} />
              </IconButton>

              <IconButton size='medium' onClick={handleClickMenu}>
                <MenuIcon />
              </IconButton>

              <Menu anchorEl={menuAnchorEl} open={menuOpened} onClose={handleCloseMenu}>
                <MenuItem onClick={handleDeleteItem} style={{ color: '#f44336' }}>
                  Supprimer
                </MenuItem>
              </Menu>
            </>
          )}
        </div>
      </div>

      <Typography variant='caption' color='textSecondary'>
        {moment(item.createdAt).format('[le] DD-MM-YY à HH:mm')}
        {employee && ` par ${employee.firstname} ${employee.lastname}`}
      </Typography>

      {item.payload?.text && (
        <Typography variant='body2' color='textSecondary' className={classes.message}>
          {(item.payload?.text || '').split('\n').map((message, key) => (
            <Fragment key={key}>
              {message}
              <br />
            </Fragment>
          ))}
        </Typography>
      )}

      {error && <div style={{ color: '#f44336' }}>{error}</div>}

      {item.type === 'POST' && (
        <div className={classes.attachments}>
          {(item.attachmentsIds?.length || 0) > 0 &&
            item.attachmentsIds!.map((attachmentId, key) => {
              const attachment = getAttachment(attachmentId);

              if (!attachment) {
                return null;
              }

              return <Attachment key={key} attachment={attachment} onDelete={() => setFileIdToDelete(attachmentId)} />;
            })}

          {attachmentsLoading && <Skeleton width={50} />}

          <Chip
            icon={<AddIcon />}
            size='small'
            variant='outlined'
            label='Fichier'
            onClick={() => inputFileRef.current?.click()}
            style={{ borderStyle: 'dashed' }}
          />

          <input style={{ display: 'none' }} ref={inputFileRef} type='file' onChange={onPickImage} />
        </div>
      )}

      <ConfirmationDialog
        title='Confirmation'
        description='Vous êtes sur le point de supprimer un fichier. Ëtes vous sûr ?'
        open={!!fileIdToDelete}
        onValidate={handleFileDelete}
        onClose={() => setFileIdToDelete(undefined)}
        confirmationText='Supprimer'
      />

      <PostEditionDialog
        item={item}
        onClose={() => setIsEditionDialogOpen(false)}
        open={isEditionDialogOpen}
        onValidate={onEditItem}
      />
    </VerticalTimelineElement>
  );
};

export default TimelineElement;
